import { Component, OnInit, EventEmitter, Input, Output, AfterViewInit, ViewChild } from '@angular/core';
import { MediaObserver, MediaChange } from '@angular/flex-layout';

import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';
import { PaymentService } from '../../services/payment.service';
import { CompanyService } from '../../services/company.service';

import { OpenNotesPaymentHistoryListItem, PendingPayment } from 'src/app/models/payment';
import { CompanyInformation } from '../../models/company';

@Component({
  selector: 'app-payment-history-list',
  templateUrl: './payment-history-list.component.html',
  styleUrls: ['./payment-history-list.component.scss']
})
export class PaymentHistoryListComponent implements OnInit, AfterViewInit {
  private _participantId = 0;
  public show = false;

  @Input()
  set participantId(participantId: number) {
    this._participantId = participantId || 0;
    this.getData();
  }
  get participantId(): number {
    return this._participantId;
  }

  @Input() displayTitle = false;

  @Input() displayNotices = false;

  @Output() selected = new EventEmitter<number>();

  // Have to use the following ElementDisplayStyle variables and ngStyle to show/hide table elements
  // This because the table paginator will not attach correctly otherwise - this is a work around for a known issue
  private _hideElementDisplayStyle = 'none';
  private _showElementDisplayStyle = 'inline';
  private _showElementDisplayStyleIE = 'inline-block';

  private _handleError: HandleError;
  private _logging = environment.logging;

  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  showSpinner = false;

  openNotesPaymentHistoryList: (OpenNotesPaymentHistoryListItem)[] = [];
  selectedNoteId: number = null;
  pendingPayment: PendingPayment;

  // DataTable
  tableDisplayedColumns = [
    'noteId',
    'noteStatus',
    'nextPaymentDueDate',
    'pastDueAmount',
    'lateFeeDue',
    'totalAmountDue',
    'payoffAmount',
    'autoPayFlag'
  ];
  tableDataSource = new MatTableDataSource<OpenNotesPaymentHistoryListItem>(this.openNotesPaymentHistoryList);
  tableDisplayStyle: string = this._hideElementDisplayStyle;
  tableDisplayStyleIE: string = this._hideElementDisplayStyle;
  openNotesPaymentHistoryListFound = false;
  convertedNoteFound = false;

  companyInformation: CompanyInformation;

  filteredNotesList = [...this.openNotesPaymentHistoryList];
  selectedNoteStatus = '';
  uniqueNoteStatuses: string[] = [];

  constructor(
    private _errorHandler: ErrorHandler,
    private _paymentService: PaymentService,
    private _companyService: CompanyService,
    public media: MediaObserver
  ) {
    this._handleError = _errorHandler.createHandleError('PaymentHistoryListComponent');
  }

  ngOnInit() {
    this.openNotesPaymentHistoryListFound = true;
    this.tableDisplayStyle = this.openNotesPaymentHistoryListFound ? this._showElementDisplayStyle : this._hideElementDisplayStyle;
    this.tableDisplayStyleIE = this.openNotesPaymentHistoryListFound ? this._showElementDisplayStyleIE : this._hideElementDisplayStyle;
  }

  ngAfterViewInit() {}

  getData(): void {
    this.showSpinner = true;

    this.companyInformation = this._companyService.getNewCompanyInformation();

    this._companyService.companyInformation.subscribe(
      response => (this.companyInformation = response),
      error => this._handleError('companyInformation.subscribe', error)
    );

    this.getPendingPayment(this._participantId);
    this.getOpenNotesPaymentHistoryList(this._participantId);
  }

  getPendingPayment(participantId: number): void {
    this.showSpinner = true;
    this._paymentService.getPendingPayment(participantId, null).subscribe(
      result => {
        this.pendingPayment = result;
        if (this._logging.serviceCallTraceLog) {
          console.log('PaymentNoteListComponent.getPendingPayment :: result.participantId = ' + result.pendingPaymentAmt);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('PaymentNoteListComponent.getPendingPayment :: result = ' + JSON.stringify(result));
        }
        //this.getDataComplete();
      },
      error => this._handleError('getPendingPayment', error)
    );
  }

  getOpenNotesPaymentHistoryList(participantId: number): void {
    this.showSpinner = true;
    this._paymentService.getOpenNotesPaymentHistoryList(participantId).subscribe(
      resultArray => {
        this.openNotesPaymentHistoryList = resultArray;
        this.filteredNotesList = [...this.openNotesPaymentHistoryList];
        this.uniqueNoteStatuses = [...new Set(this.openNotesPaymentHistoryList.map(note => note.noteStatus))];
        
        if (this._logging.serviceCallTraceLog) {
          console.log('PaymentHistoryListComponent.getOpenNotesPaymentHistoryList :: result.length = ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('PaymentHistoryListComponent.getOpenNotesPaymentHistoryList :: result = ' + JSON.stringify(resultArray));
        }
        this.selectedNoteId = null;
        this.tableDataSource = new MatTableDataSource<OpenNotesPaymentHistoryListItem>(this.openNotesPaymentHistoryList);
        this.openNotesPaymentHistoryListFound = resultArray.length > 0;

        // Check for Conversion Trades  - 10/21/2019
        const openNotesPaymentHistoryListConvertedNote = resultArray.find(x => x.conversionnote === '*');
        if (openNotesPaymentHistoryListConvertedNote !== undefined) {
          this.convertedNoteFound = false;
          this.toggle();
        } else {
            this.convertedNoteFound = true;
            this.toggle();
          }

        this.tableDisplayStyle = this.openNotesPaymentHistoryListFound ? this._showElementDisplayStyle : this._hideElementDisplayStyle;
        this.tableDisplayStyleIE = this.openNotesPaymentHistoryListFound ? this._showElementDisplayStyleIE : this._hideElementDisplayStyle;
        this.getDataComplete();
      },
      error => this._handleError('getOpenNotesPaymentHistoryList', error)
    );
  }

  filterNotes() {

    // this.selectedNoteId = null;

    if (this.selectedNoteStatus) {
      this.filteredNotesList = this.openNotesPaymentHistoryList.filter(
        note => note.noteStatus === this.selectedNoteStatus
      );
    } else {
      this.filteredNotesList = [...this.openNotesPaymentHistoryList];
    }
  }

  // 10/22/2019 - Check if Conversion Trade - if so display Footnote, else hide footnote
  toggle() {
    if (this.convertedNoteFound  === true) {this.show = this.show; } else {this.show =  !this.show; }
  }

  getDataComplete(): void {
    this.showSpinner = false;
  }

  selectNote(noteId: number) {
    this.selectedNoteId = noteId;
    this.selected.emit(noteId);
  }

  getToolTip(element: string): string {
    let toolTip: string;
    switch (element.toUpperCase().trim()) {
      case 'NOTESTATUS': {
        toolTip = 'What does My Loan Status Mean?';
        break;
      }
      default: {
        toolTip = 'No ToolTip message has been defined for ' + element;
        break;
      }
    }
    return toolTip;
  }

  openToolTip(element: string): void {
    switch (element.toUpperCase().trim()) {
      case 'NOTESTATUS': {
        if (this._logging.navigationTraceLog) {
          console.log('PaymentHistoryListComponent.openToolTip');
        }
        window.open(this.companyInformation.loanStatusDescriptionsURL, '_blank');
        break;
      }
    }
  }
}
